import React from 'react';
import { get, isEmpty, isNumber, minBy } from 'lodash-es';
import { SITE_URL } from 'site-modules/shared/pages/seo';
import { ConsumerReviewsModel, ConsumerReviewsPaths } from 'client/data/models/consumer-reviews';
import {
  EditorialReviewModel,
  buildReviewPath,
  buildAuthorPath,
  buildPreProdContentPath,
  buildRatedByPath,
} from 'client/data/models/editorial-review';
import { VehicleDetailsModel, buildWhatsNewPath } from 'client/data/models/mmy-details';
import { buildShortsPath, buildVideosPath, MediaModel } from 'client/data/models/media';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import {
  buildVehiclePath,
  buildPopularStylesPath,
  buildMakeModelDefaultYear,
  buildUsedTmvPricingPath,
  getUsedBaseStylePath,
  buildEvTestedDataPath,
  buildFuelCostsPath,
  buildMmyModelFamilyIdsPath,
  buildModelFamilyIdsModelYearsPath,
  FeatureSpecsPaths,
  VehicleModel,
  buildMMYGenericFeaturesPath,
  buildForSaleYearsPath,
} from 'client/data/models/vehicle-v2';
import { AppraisalModel, buildAllStylesAppraisalFilteredPath } from 'client/data/models/appraisal';
import { VisitorModel } from 'client/data/models/visitor';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { SafetyModel, buildSafetyRatingsPathByParams } from 'client/data/models/safety';
import { getRepairPalDataPath, ReliabilityModel } from 'client/data/models/reliability';
import { MaintenanceModel, MaintenancePaths } from 'client/data/models/maintenance';
import { buildMakeArticlesPath, EditorialModel } from 'client/data/models/editorial';
import { InventoryModel } from 'client/data/models/inventory';
import { EvInsightsModel, EvInsightsPaths, INVENTORY_CODE } from 'client/data/models/ev-insights';
import { InventoryAggregateModel, buildMMYSubmodelTypeAmountsPath } from 'client/data/models/inventory-aggregate';
import {
  buildSubmodelsPath,
  buildVehiclePath as buildGraphqlVehiclePath,
  VehicleGraphQLModel,
} from 'client/data/models/vehicle-graphql';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
import { SEOModel } from 'client/data/models/seo';
import { getModelState } from 'client/data/luckdragon/model';
import { buildCoreBreadcrumbs } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getCoreUrlPattern } from 'site-modules/shared/utils/core-page/url-pattern';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getCarVinSchema, getCorePageCarSchema } from 'client/seo/schemas/car';
import { getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { isUsedCoreMmyYear } from 'site-modules/shared/utils/core-page/used-core-mmy';
import {
  buildMMYSubmodelTrimPhotosPathWithParams,
  buildSubmodelEvoxPhotoPath,
  PhotosModel,
} from 'client/data/models/photos';
import { buildSimilarVehiclesCrossShopPath, CoreResearchModel } from 'client/data/models/core-research';
import { getTreehouseEligiblePath, TreehouseModel } from 'client/data/models/treehouse';
import { buildAllPhotosPath, buildSpinPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { getWizardIncentivesPath, IncentiveModel } from 'client/data/models/incentives';
import { formatPriceString } from 'client/site-modules/shared/utils/price-utils';
import { EdmundsNewCarsNoAdhesion } from 'client/site-modules/shared/pages/decorators/edmunds-new-cars-no-adhesion';
import { appendReviewSchema } from 'client/seo/schemas/review';
import { getImageSchema } from 'client/seo/schemas/image';
import { getImageUrl, getMediaImageUrl, IMAGE_SIZE_AND_EXT_REG, SOURCES } from 'client/utils/image-helpers';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'client/site-modules/shared/components/native-ad/utils/utils';
import { PageModel } from 'client/data/models/page';
import {
  INCENTIVES_AD,
  BUILD_PRICE_AD,
  PRICING_AD,
  SRP_AD,
} from 'client/site-modules/shared/components/native-ad/utils/constants';
import { CmsModel } from 'client/data/models/cms';
import { setCriticalCss } from 'client/actions/critical-css';
import { corePageDecorator } from 'site-modules/shared/utils/core-page/core-page-decorator-v2';
import {
  CORE_GALLERY_SIZE,
  SEO_IMAGES_PAGE_SIZE,
  SEO_IMAGES_WIDTHS,
  VEHICLE_MILEAGE_PER_YEAR,
} from 'client/site-modules/core-page/utils/constants';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getUsedVehiclePriceRangeFromModelState } from 'site-modules/shared/utils/core-page/pricing';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { CAR_NEWS_LANDING_CONTENT_PATH } from 'client/site-modules/shared/constants/editorial/editorial';
import { OLD_REVIEW_FORMAT_LAST_YEAR } from 'site-modules/shared/constants/editorial-review';
import { CATEGORIES_PLAIN } from 'site-modules/shared/constants/photoflipper/photoflipper';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getPreloadHeroImageAsset } from 'client/site-modules/core-page/utils/get-preload-hero-image-asset';
import { cloneParamsWithPubState } from 'site-modules/shared/utils/core-page/page-helpers';
import { getCoreMedia } from 'site-modules/shared/utils/core-page/core-media';
import {
  CORE_SRP_INVENTORY_COUNT,
  getInitialSearchFilter,
  getUnknownLocationSearchFilter,
} from 'site-modules/core-page/utils/core-srp';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';
import { getQuery } from 'client/utils/location';
import { CPO_CARS } from 'site-modules/shared/utils/cpo-message';

import { UsedCore } from './used-core';

const getPageOptions = params => ({
  name: params.isNoSubmodelPage ? 'used_model_core_review' : 'used_model_core',
  category: 'used_model_core',
  chunkName: 'used_model_core',
  preload: (store, props) => {
    const { isNoSubmodelPage, year } = params;
    const query = getQuery(props.location);
    const { mobile, featureFlags } = store.getState();

    const state = store.getState();
    const isCore7646Enabled = featureFlags?.['core-7646-used-graphql-vehicle'];

    const preloader = new VehiclePreloader(
      store,
      { ...props, params },
      {
        isSubpage: false,
        useGraphqlVehicle: isCore7646Enabled,
      }
    );
    const urlPattern = getCoreUrlPattern(
      params.year,
      params.submodel,
      isNoSubmodelPage && !isUsedCoreMmyYear(year) && 'review'
    );
    const isMakeModelYearReview = urlPattern === 'make/model/year/review/';

    preloader.resolve('location', VisitorModel);

    preloader.set(URL_CONTEXT_PATH, UrlModel, { urlPattern });
    const enableSeot5073 = get(state, 'featureFlags.enable-seot-5073-used-core-llm-content-gen');
    if (enableSeot5073 || isMakeModelYearReview) {
      preloader.resolve('content', SEOModel);
    }
    return preloader.load().then(modelState => {
      const makeModelSubmodelYear = isCore7646Enabled
        ? modelState.get(buildGraphqlVehiclePath(params), VehicleGraphQLModel)
        : modelState.get(buildVehiclePath(params), VehicleModel);
      const {
        make: { slug: make },
        model: { slug: model, modelLinkCode },
        modelYearId,
      } = makeModelSubmodelYear;

      const vehicleParams = getParamsFromVehicle(makeModelSubmodelYear);
      const photoParams = {
        ...vehicleParams,
        submodel: makeModelSubmodelYear.submodels.name,
      };

      preloader.resolve(FeatureSpecsPaths.buildPartialFeaturesPath(vehicleParams), VehicleModel);
      preloader.resolve(buildAllStylesAppraisalFilteredPath(vehicleParams), AppraisalModel);
      preloader.resolve(buildMakeModelDefaultYear(vehicleParams), VehicleModel);
      preloader.resolve(buildMMYSubmodelTypeAmountsPath(params), InventoryAggregateModel);
      preloader.resolve(getUsedBaseStylePath(params), VehicleModel);

      // cost-to-drive
      preloader.resolve(buildPopularStylesPath(params), VehicleModel);
      // core highlights,ev insights,trims-comparator link
      preloader.resolve(buildPopularStylesPath(vehicleParams), VehicleModel);

      // core-highlights,reliability
      preloader.resolve(getRepairPalDataPath({ makeSlug: make, modelSlug: model, year }), ReliabilityModel);
      // reliability
      preloader.resolve(MaintenancePaths.buildMaintenanceRecallsPath(modelYearId), MaintenanceModel);

      preloader.resolve(buildReviewPath(vehicleParams), EditorialReviewModel);
      preloader.resolve(buildAuthorPath(vehicleParams), EditorialReviewModel);
      preloader.resolve(buildRatedByPath(vehicleParams), EditorialReviewModel);

      preloader.resolve(buildSimilarVehiclesCrossShopPath(modelLinkCode), CoreResearchModel);
      preloader.resolve(buildSafetyRatingsPathByParams({ params: vehicleParams }), SafetyModel);
      preloader.resolve(buildWhatsNewPath(vehicleParams), VehicleDetailsModel);
      preloader.resolve(buildPreProdContentPath(vehicleParams), EditorialReviewModel);
      preloader.resolve(buildVideosPath(vehicleParams), MediaModel);
      preloader.resolve(buildShortsPath(vehicleParams), MediaModel);
      preloader.resolve(buildSubmodelsPath(vehicleParams), VehicleGraphQLModel);
      if (isCore7646Enabled) {
        if (!isEmpty(makeModelSubmodelYear.modelFamilyIds)) {
          preloader.resolve(buildModelFamilyIdsModelYearsPath(makeModelSubmodelYear.modelFamilyIds), VehicleModel);
        }
      } else {
        preloader.resolve(buildMmyModelFamilyIdsPath(vehicleParams), VehicleModel);
      }
      preloader.resolve(buildForSaleYearsPath(vehicleParams), VehicleModel);
      preloader.resolve(buildSpinPhotosPath(photoParams), PhotoflipperModel);

      // car-news widget
      preloader.resolve(`content["${CAR_NEWS_LANDING_CONTENT_PATH}"]`, CmsModel);
      preloader.resolve(buildMakeArticlesPath(make), EditorialModel);

      // page-links widget
      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

      if (CPO_CARS[make]?.[model]?.[year]) {
        preloader.resolve(`cpoData["${make}"]`, InventoryModel);
      }

      const adsList = [BUILD_PRICE_AD.AD_NAME, INCENTIVES_AD.AD_NAME, SRP_AD.AD_NAME];
      if (year > OLD_REVIEW_FORMAT_LAST_YEAR) {
        adsList.push(PRICING_AD.AD_NAME);
      }

      const vehiclePageContext = modelState.get('vehicle', PageModel);
      setAdsListToPreload(preloader, store.getState(), adsList, vehiclePageContext);

      // SEO photos
      preloader.resolve(buildSubmodelEvoxPhotoPath(photoParams), PhotosModel);
      preloader.resolve(
        buildMMYSubmodelTrimPhotosPathWithParams({
          ...photoParams,
          pageSize: SEO_IMAGES_PAGE_SIZE,
          width: SEO_IMAGES_WIDTHS,
        }),
        PhotosModel
      );

      // Hero image
      const heroImageWidth = mobile ? CORE_GALLERY_SIZE.MOBILE.HERO.width : CORE_GALLERY_SIZE.DESKTOP.HERO.width;
      preloader.resolve(
        buildAllPhotosPath({
          ...photoParams,
          pageNum: 1,
          imageSize: heroImageWidth,
        }),
        PhotoflipperModel
      );

      // Gallery row
      const rowImageSize = mobile ? CORE_GALLERY_SIZE.MOBILE.ROW : CORE_GALLERY_SIZE.DESKTOP.ROW;
      preloader.resolve(
        buildAllPhotosPath({
          ...photoParams,
          pageNum: 1,
          imageSize: rowImageSize.width,
        }),
        PhotoflipperModel
      );

      preloader.resolve(
        buildAllPhotosPath({
          ...photoParams,
          pageNum: 1,
          category: CATEGORIES_PLAIN.INTERIOR,
          imageSize: rowImageSize.width,
        }),
        PhotoflipperModel
      );

      // consumer-reviews
      preloader.set(ConsumerReviewsPaths.getVehicleFilterPath(), ConsumerReviewsModel, {
        params: { make: params.make, model: params.model, year: params.year, submodel: params.submodel },
      });
      preloader.set(ConsumerReviewsPaths.getConsumerReviewsFilterPath(), ConsumerReviewsModel, {
        pagenum: 1,
        pagesize: 4, // Number of consumer reviews in component
      });
      preloader.resolve(ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath(vehicleParams), ConsumerReviewsModel);
      preloader.resolve(
        ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath({ ...vehicleParams, year: year - 1 }),
        ConsumerReviewsModel
      );
      preloader.resolve(ConsumerReviewsPaths.buildReviewsSummaryPath(vehicleParams), ConsumerReviewsModel);
      preloader.resolve(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel);

      const isInternational = isUnknownZip(modelState.get('location', VisitorModel));
      const isCore6829Enabled =
        isInternational &&
        ExperimentUtil.getForcedOrAssignedRecipeName({
          state: store.getState(),
          campaignName: 'core-6829-used-nozip',
          defaultVal: 'ctrl',
        }) === 'chal';

      preloader.set('searchResultsFilter', InventoryModel, {
        ...getUnknownLocationSearchFilter(isCore6829Enabled),
        ...getInitialSearchFilter({ makeSlug: make, modelSlug: model, year }),
        ...getFilterFromLocation(query),
        pageSize: CORE_SRP_INVENTORY_COUNT[!!mobile],
        fetchSuggestedFacets: true,
      });
      preloader.resolve('searchResults', InventoryModel);

      const estimatedMileage = VEHICLE_MILEAGE_PER_YEAR * (new Date().getFullYear() - year);
      preloader.set('estimatedAppraisalMileage', VehicleModel, estimatedMileage);

      return preloader.load().then(async modelStateInstance => {
        const submodelAmounts = modelStateInstance.get(
          buildMMYSubmodelTypeAmountsPath(params),
          InventoryAggregateModel
        );
        const hasPriceRange = get(submodelAmounts, `priceRanges["${makeModelSubmodelYear.submodels.slug}"]`);

        if (isInternational || !hasPriceRange) {
          const usedBaseStyleId = modelStateInstance.get(
            `${getUsedBaseStylePath(params)}.usedDefaultStyle.id`,
            VehicleModel
          );

          preloader.resolve(buildUsedTmvPricingPath(usedBaseStyleId), VehicleModel);
        }

        // Other years
        if (!isCore7646Enabled) {
          const modelFamilyIds = modelStateInstance.get(buildMmyModelFamilyIdsPath(vehicleParams), VehicleModel);
          if (!isEmpty(modelFamilyIds)) {
            preloader.resolve(buildModelFamilyIdsModelYearsPath(modelFamilyIds), VehicleModel);
          }
        }

        // Range and Cost/EV Insights: start
        const stylesFeatures = modelStateInstance.get(
          FeatureSpecsPaths.buildPartialFeaturesPath(vehicleParams),
          VehicleModel
        );

        const popularStyles = modelStateInstance.get(
          buildPopularStylesPath(getParamsFromVehicle(makeModelSubmodelYear)),
          VehicleModel
        );
        const isElectric = isCore7646Enabled
          ? !!makeModelSubmodelYear.styleAttributes?.isElectric
          : !!get(stylesFeatures, '[0].styleAttributes.electric');
        const lowestPricedStyleId = isCore7646Enabled
          ? makeModelSubmodelYear.defaultStyleId
          : get(minBy(popularStyles, 'price.baseMSRP') || popularStyles?.[0], 'id');

        preloader.resolve(buildFuelCostsPath(lowestPricedStyleId), VehicleModel);
        if (isElectric) {
          preloader.resolve(
            EvInsightsPaths.getEvInsightsDataPath(lowestPricedStyleId, INVENTORY_CODE.USED),
            EvInsightsModel
          );
          preloader.resolve(getWizardIncentivesPath(lowestPricedStyleId, true), IncentiveModel);

          if (year >= EV_TESTED_DATA_START_DATE) {
            preloader.resolve(buildEvTestedDataPath([lowestPricedStyleId]), VehicleModel);
          }
        }
        // Range and Cost/EV Insights: end

        // Consumer reviews
        const reviewSummary = modelStateInstance.get(
          ConsumerReviewsPaths.buildReviewsSummaryPath(vehicleParams),
          ConsumerReviewsModel
        );
        if (!reviewSummary) {
          preloader.resolve(
            ConsumerReviewsPaths.buildConsumerReviewsAspectsPath(makeModelSubmodelYear),
            ConsumerReviewsModel
          );
        }

        // Features-highlights
        preloader.resolve(buildFuelCostsPath(get(stylesFeatures, '[0].id')), VehicleModel);

        if (
          get(stylesFeatures, '[0].styleAttributes.electric') ||
          get(stylesFeatures, '[0].styleAttributes.pluginElectric')
        ) {
          preloader.resolve(getWizardIncentivesPath(get(stylesFeatures, '[0].id'), true), IncentiveModel);

          // treehouse promo
          preloader.resolve(getTreehouseEligiblePath(), TreehouseModel);
        }

        // Safety
        const editorialReview = modelStateInstance.get(buildReviewPath(params), EditorialReviewModel);
        if (isEmpty(get(editorialReview, 'safetyFeatures'))) {
          preloader.resolve(buildMMYGenericFeaturesPath(params), VehicleModel);
        }

        const siteServedAds = modelStateInstance.get(
          getCreativeConfigAdPathByVehicle(mobile, vehiclePageContext),
          NativeAdsCreativeConfigModel
        );

        const hasEditorialSummary = !!editorialReview?.ratings?.overall?.text;

        store.dispatch(
          setCriticalCss({
            criticalCssHeight: 2050,
            ...siteServedAdsCriticalCss(siteServedAds, [INCENTIVES_AD.AD_NAME]),
            isInternational: !isCore6829Enabled && isInternational,
            hasEditorialSummary,
          })
        );

        if (isNoSubmodelPage) {
          preloader.set('server.preloadedAssets', PageModel, [
            getPreloadHeroImageAsset({
              modelState: modelStateInstance,
              makeModelSubmodelYear,
              photoParams,
              heroImageWidth,
              params,
              year,
              mobile,
            }),
          ]);
        }

        return preloader.load();
      });
    });
  },
  seo: state => {
    const { isNoSubmodelPage } = params;
    const { mobile } = state;
    const modelState = getModelState(state);
    const isCore7646Enabled = modelState.get('core-7646-used-graphql-vehicle', FeatureFlagModel);
    const makeModelSubmodelYear = isCore7646Enabled
      ? modelState.get(buildGraphqlVehiclePath(params), VehicleGraphQLModel)
      : modelState.get(buildVehiclePath(params), VehicleModel);
    const inventories = modelState.get(`vins`, InventoryModel);

    const seoObject = { jsonld: [], keywords: '' };
    const seoContent = modelState.get('content', SEOModel);
    const customDateModified = seoContent?.dateModified;

    if (makeModelSubmodelYear) {
      const vehicleParams = getParamsFromVehicle(makeModelSubmodelYear);
      const photoParams = {
        ...vehicleParams,
        submodel: makeModelSubmodelYear.submodels.name,
      };
      const editorialReview = modelState.get(buildReviewPath(vehicleParams), EditorialReviewModel);
      const editorialReviewAuthor = modelState.get(buildAuthorPath(vehicleParams), EditorialReviewModel);
      const consumerReviews = modelState.get(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel);
      const whatsNewContent = modelState.get(buildWhatsNewPath(vehicleParams), VehicleDetailsModel);
      const displayVehicle = buildDisplayVehicle({ makeModelSubmodelYear, params, disableTrademarkCharacter: true });
      const featuresAndSpecs = modelState.get(FeatureSpecsPaths.buildPartialFeaturesPath(vehicleParams), VehicleModel);
      const styleAttributes = featuresAndSpecs?.[0]?.styleAttributes;

      const metaTagsDisplayVehicle = buildDisplayVehicle({
        makeModelSubmodelYear,
        params,
        disableTrademarkCharacter: true,
        updateChevroletMakeName: true,
        usedPrefix: '',
      });

      const pageUrl = `${SITE_URL}${getCoreUrl({ makeSlug: params.make, modelSlug: params.model, year: params.year }, true)}`;
      const alwaysIncludeReview = pageUrl?.endsWith('/review/');

      if (isNoSubmodelPage) {
        const model = get(makeModelSubmodelYear, 'model.name', '');
        const make = get(makeModelSubmodelYear, 'make.name', '');
        seoObject.canonical = pageUrl;
        seoObject.title = `${metaTagsDisplayVehicle} Review & Ratings | Edmunds`;
        seoObject.description = `Edmunds' expert review of the Used ${metaTagsDisplayVehicle} provides the latest look at trim-level features and specs, performance, safety, and comfort. At Edmunds we drive every car we review, performing road tests and competitor comparisons to help you find your perfect car. Read the full in-depth review of the Used ${
          params.year
        } ${model} at Edmunds.`;
        seoObject.keywords = `${params.year} ${make} ${model} review`;
      } else {
        const amounts = modelState.get(buildMMYSubmodelTypeAmountsPath(params), InventoryAggregateModel);
        const maxSavings = amounts && isNumber(amounts.maxSavings) ? amounts.maxSavings : null;
        const inventoryCount = amounts && amounts.inventoryCount;

        const savingsStr =
          maxSavings && inventoryCount
            ? `Save up to ${formatPriceString(maxSavings)} on one of ${inventoryCount} ${displayVehicle} near you`
            : `Save money on ${displayVehicle} models near you`;

        seoObject.description = `Edmunds has detailed price information for the ${displayVehicle}. ${savingsStr}. Find detailed gas mileage information, insurance estimates, and more.`;
        seoObject.title = `${displayVehicle} Review | Edmunds`;
      }

      // Use original params for breadcrumbs
      seoObject.jsonld.push(
        getBreadcrumbSchema(
          buildCoreBreadcrumbs({ makeModelSubmodelYear, params, includeLastLink: true, isMainCorePage: true })
        )
      );

      const priceRange = getUsedVehiclePriceRangeFromModelState({
        modelState,
        params,
        submodelSlug: makeModelSubmodelYear.submodels.slug,
      });

      const seoImages = modelState.get(
        buildMMYSubmodelTrimPhotosPathWithParams({
          ...photoParams,
          pageSize: SEO_IMAGES_PAGE_SIZE,
          width: SEO_IMAGES_WIDTHS,
        }),
        PhotosModel
      );
      const images = seoImages?.map(({ href }) => ({
        image: getMediaImageUrl(href),
        thumb: getMediaImageUrl(href.replace(IMAGE_SIZE_AND_EXT_REG, '_175.jpg')),
      }));
      const heroImageWidth = mobile ? CORE_GALLERY_SIZE.MOBILE.HERO.width : CORE_GALLERY_SIZE.DESKTOP.HERO.width;
      const firstContent = modelState.get(
        buildPreProdContentPath(getParamsFromVehicle(makeModelSubmodelYear)),
        EditorialReviewModel
      );
      const heroPhotos = modelState.get(
        buildAllPhotosPath({
          ...photoParams,
          pageNum: 1,
          imageSize: heroImageWidth,
        }),
        PhotoflipperModel
      );
      const { heroImage } = getCoreMedia({
        params: cloneParamsWithPubState({
          params,
          year: params.year,
          isUsed: true,
        }),
        vehicle: makeModelSubmodelYear,
        firstContent,
        editorialReviewHero: get(editorialReview, 'heroImage'),
        photos: heroPhotos,
        isPreprod: false,
        defaultSize: mobile ? CORE_GALLERY_SIZE.MOBILE.HERO : CORE_GALLERY_SIZE.DESKTOP.HERO,
      });
      const seoHeroUrl = getImageUrl(heroImage.src, undefined, SOURCES.MEDIA);

      const carSchema = appendReviewSchema(
        getCorePageCarSchema({
          makeModelSubmodelYear,
          editorialReview,
          consumerReviews,
          seoObject,
          priceRange,
          whatsNewContent,
          url: pageUrl,
          images,
          heroImage: seoHeroUrl,
          isCorePage: true,
        }),
        {
          params: {
            ...params,
            isElectric: !!styleAttributes?.electric,
            isPluginHybrid: !!styleAttributes?.pluginElectric,
            isTruck: !!styleAttributes?.truck,
          },
          makeModelSubmodelYear,
          editorialReview,
          author: editorialReviewAuthor,
          consumerReviews,
          priceRange,
          hasItemReviewed: false,
          thumbUrl: images?.[0]?.thumb,
          isCorePage: true,
          customDateModified,
          alwaysIncludeReview,
        }
      );

      seoObject.jsonld.push(carSchema);

      const photos = modelState.get(
        buildSubmodelEvoxPhotoPath({
          ...photoParams,
        }),
        PhotosModel
      );
      const photo = get(photos, '[0]');

      if (photo) {
        seoObject.jsonld.push(
          getImageSchema({
            uri: getMediaImageUrl(photo),
            title: displayVehicle,
          })
        );
      }
    }

    if (!isNoSubmodelPage) {
      if (inventories && Array.isArray(inventories.results)) {
        inventories.results.forEach(result => {
          const carVinSchema = getCarVinSchema({ vinData: result });
          delete carVinSchema.aggregateRating;
          seoObject.jsonld.push(carVinSchema);
        });
      }
    }

    return seoObject;
  },
  component: props => <UsedCore {...props} params={params} />,
  decorator: EdmundsNewCarsNoAdhesion,
  options: {
    disableScrollToTop: true,
    hasVehicleContext: true,
    isCore7646Test: true,
  },
});

export default params => corePageDecorator(getPageOptions(params), params);
